import type { Product, ProductAttribute } from "~/data/products";
import { appendUpstreamPriceParams } from "~/lib/catalog-query";
import { fetchWrapper } from "~/lib/fetch";

/** One row in `data.attributes` from customer-products API */
export interface ApiProductAttribute {
  id?: number;
  name?: string;
  notes?: string;
  price?: number;
  value?: string;
  images?: string[] | null;
  quantity?: number | string;
  business_id?: string | number;
  businessId?: string | number;
  [key: string]: unknown;
}

/** Raw product shape from customer-products API */
export interface ApiProduct {
  /** Primary id; some payloads use `product_id` / `uuid` only — see `coerceProductId`. */
  id?: string;
  product_id?: string;
  uuid?: string;
  business_id?: string | number;
  name?: string;
  description?: string;
  main_photo?: string;
  other_photos?: string[];
  quantity?: string;
  unit_base_price?: number;
  currency?: string;
  include_tax?: boolean;
  product_type?: string;
  other_details?: Record<string, unknown>;
  status?: string;
  created_at?: string;
  last_updated?: string;
  image?: string;
  price?: string;
  attributes?: ApiProductAttribute[];
  [key: string]: unknown;
}

const DEFAULT_BUCKET =
  "https://objectstorage.apps.henga.co/diaspora-dollar/";

/** UUID v4-style ids from customer-products; rejects junk like `installHook.js.map`. */
const CUSTOMER_PRODUCT_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isValidCustomerProductIdQuery(raw: string): boolean {
  return CUSTOMER_PRODUCT_UUID_RE.test(raw.trim());
}

/** Normalize API business / store ids (string, number, or alternate JSON keys). */
export function normalizeBusinessIdFromUnknown(raw: unknown): string | undefined {
  if (raw == null) return undefined;
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return String(Math.trunc(raw));
  }
  if (typeof raw === "string") {
    const t = raw.trim();
    return t.length > 0 ? t : undefined;
  }
  return undefined;
}

/** Prefer variant-level store id, then product-level (for cart `businessId`). */
export function pickBusinessIdForCartLine(
  product: Product,
  attribute?: Pick<ProductAttribute, "business_id"> | undefined,
): string | undefined {
  const fromAttr = normalizeBusinessIdFromUnknown(attribute?.business_id);
  const fromProduct = normalizeBusinessIdFromUnknown(product.business_id);
  return fromAttr ?? fromProduct;
}

function resolveProductVendorName(api: ApiProduct): string | undefined {
  const r = api as Record<string, unknown>;
  const nestedBusiness = r.business;
  if (nestedBusiness && typeof nestedBusiness === "object" && !Array.isArray(nestedBusiness)) {
    const maybeName = (nestedBusiness as Record<string, unknown>)["name"];
    if (typeof maybeName === "string" && maybeName.trim()) return maybeName.trim();
  }
  const candidates = [
    r.business_name,
    r.businessName,
    r.vendor_name,
    r.vendor,
  ];
  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) return c.trim();
  }
  if (
    typeof api.other_details === "object" &&
    api.other_details !== null &&
    !Array.isArray(api.other_details)
  ) {
    const od = api.other_details as Record<string, unknown>;
    for (const key of ["business_name", "businessName", "vendor_name", "vendor"] as const) {
      const v = od[key];
      if (typeof v === "string" && v.trim()) return v.trim();
    }
  }
  return undefined;
}

function resolveProductBusinessId(api: ApiProduct): string | undefined {
  const r = api as Record<string, unknown>;
  return (
    normalizeBusinessIdFromUnknown(api.business_id) ??
    normalizeBusinessIdFromUnknown(r.businessId) ??
    normalizeBusinessIdFromUnknown(r.Business_id) ??
    normalizeBusinessIdFromUnknown(r.merchant_id) ??
    (typeof api.other_details === "object" &&
    api.other_details !== null &&
    !Array.isArray(api.other_details)
      ? normalizeBusinessIdFromUnknown(
          (api.other_details as Record<string, unknown>).business_id,
        ) ??
        normalizeBusinessIdFromUnknown(
          (api.other_details as Record<string, unknown>).businessId,
        )
      : undefined)
  );
}

/** Resolve product UUID from common API field names. */
function coerceProductId(api: ApiProduct): string {
  const r = api as Record<string, unknown>;
  const candidates: unknown[] = [
    api.id,
    api.product_id,
    api.uuid,
    r.productId,
    r.product_id,
    r.uuid,
  ];
  for (const c of candidates) {
    if (typeof c === "string") {
      const t = c.trim();
      if (t.length > 0) return t;
    }
    if (typeof c === "number" && Number.isFinite(c)) {
      return String(Math.trunc(c));
    }
  }
  return "";
}

function parseStockQuantity(raw: unknown): number | undefined {
  if (raw == null) return undefined;
  if (typeof raw === "number" && Number.isFinite(raw) && raw >= 0) {
    return Math.floor(raw);
  }
  if (typeof raw === "string") {
    const n = parseInt(raw.trim(), 10);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  return undefined;
}

function mapApiAttributes(
  api: ApiProduct,
  bucketBase: string,
  parentBusinessId: string | undefined,
): ProductAttribute[] | undefined {
  const raw = api.attributes;
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  const bucket = bucketBase.replace(/\/?$/, "/");
  const currency = api.currency ?? "";
  return raw.map((a, idx) => {
    const row = a as ApiProductAttribute & Record<string, unknown>;
    const id = typeof a.id === "number" ? a.id : Number(a.id);
    const images = Array.isArray(a.images)
      ? a.images.map((p) => `${bucket}${String(p).trim()}`)
      : undefined;
    const attrQty = parseStockQuantity(a.quantity);
    const rowBiz =
      normalizeBusinessIdFromUnknown(row.business_id) ??
      normalizeBusinessIdFromUnknown(row.businessId);
    const attrBusinessId = rowBiz ?? parentBusinessId;
    return {
      id: Number.isFinite(id) ? id : idx,
      name: String(a.name ?? ""),
      value: String(a.value ?? ""),
      ...(typeof a.notes === "string" && a.notes.trim()
        ? { notes: a.notes.trim() }
        : {}),
      ...(typeof a.price === "number"
        ? {
            priceLabel: `${currency}${a.price}`,
            unitPrice: Math.round(a.price),
          }
        : {}),
      ...(attrQty != null ? { quantity: attrQty } : {}),
      ...(images?.length ? { images } : {}),
      ...(attrBusinessId ? { business_id: attrBusinessId } : {}),
    };
  });
}

export function apiProductToProduct(
  api: ApiProduct,
  bucketBase: string = DEFAULT_BUCKET,
): Product {
  const bucket = bucketBase.replace(/\/?$/, "/");
  const image =
    api.image ??
    (api.main_photo ? `${bucket}${api.main_photo}` : "");
  const price =
    api.price ??
    `${api.currency ?? ""}${api.unit_base_price ?? 0}`;
  const product_type = api.product_type ?? "";
  const imageThumbs = Array.isArray(api.other_photos)
    ? api.other_photos.map((p) => `${bucket}${String(p).trim()}`)
    : undefined;
  const resolvedId = coerceProductId(api);
  if (!resolvedId) {
    throw new Error("customer-products: API row has no id / product_id / uuid");
  }
  const resolvedBusinessId = resolveProductBusinessId(api);
  const resolvedVendorName = resolveProductVendorName(api);
  const attributes = mapApiAttributes(api, bucketBase, resolvedBusinessId);
  const quantity = parseStockQuantity(api.quantity);
  return {
    id: resolvedId,
    name: api.name ?? "",
    description: api.description ?? "",
    price,
    image,
    product_type,
    ...(resolvedBusinessId != null ? { business_id: resolvedBusinessId } : {}),
    ...(resolvedVendorName ? { vendor_name: resolvedVendorName } : {}),
    ...(imageThumbs?.length ? { imageThumbs } : {}),
    ...(attributes?.length ? { attributes } : {}),
    ...(quantity != null ? { quantity } : {}),
  };
}

export interface FetchCustomerProductsParams {
  limit: number;
  offset: number;
  category?: string;
  product_type?: string;
  /** Filter by product UUID (sent as `id` and `product_id` query params). */
  id?: string;
  /** Server-side search query (query param `search`). */
  search?: string;
  /** Comma-separated on the wire (`business_ids`). */
  business_ids?: string[];
  price_min?: number;
  price_max?: number;
}

export interface FetchCustomerProductsResult {
  products: Product[];
  hasMore: boolean;
}

/** API pages sometimes repeat the same product id; keep first occurrence. */
function dedupeProductsById(products: Product[]): Product[] {
  const seen = new Set<string>();
  const out: Product[] = [];
  for (const p of products) {
    if (seen.has(p.id)) continue;
    seen.add(p.id);
    out.push(p);
  }
  return out;
}

export async function fetchCustomerProducts(
  apiBase: string,
  bucketBase: string,
  params: FetchCustomerProductsParams,
): Promise<FetchCustomerProductsResult> {
  const {
    limit,
    offset,
    category,
    product_type,
    id: idParam,
    search,
    business_ids,
    price_min,
    price_max,
  } = params;
  const base = (apiBase ?? "").trim();
  if (!base) {
    console.warn("fetchCustomerProducts: empty api base");
    return { products: [], hasMore: false };
  }

  let url: URL;
  try {
    url = new URL(`${base.replace(/\/$/, "")}/customer-products`);
  } catch {
    console.warn("fetchCustomerProducts: invalid api base URL");
    return { products: [], hasMore: false };
  }

  url.searchParams.set("limit", String(limit));
  url.searchParams.set("offset", String(offset));
  if (category) url.searchParams.set("category", category);
  if (product_type) url.searchParams.set("product_type", product_type);
  const idTrimmed =
    typeof idParam === "string"
      ? idParam.trim()
      : idParam != null
        ? String(idParam).trim()
        : "";
  if (idTrimmed.length > 0 && isValidCustomerProductIdQuery(idTrimmed)) {
    // Upstreams vary: some expect `id`, others `product_id` — send both.
    url.searchParams.set("id", idTrimmed);
    url.searchParams.set("product_id", idTrimmed);
  }
  if (typeof search === "string" && search.trim())
    url.searchParams.set("search", search.trim());
  if (business_ids?.length) {
    url.searchParams.set("business_ids", business_ids.join(","));
  }
  appendUpstreamPriceParams(url, { price_min, price_max });

  const res = await fetchWrapper(url.toString(), { cache: "no-store" });
  if (!res.ok) {
    console.warn(
      `fetchCustomerProducts: HTTP ${res.status} ${res.statusText} (${url})`,
    );
    return { products: [], hasMore: false };
  }

  type ProductsJson = {
    code?: number;
    message?: string;
    data?: ApiProduct[] | ApiProduct;
  };
  let json: ProductsJson;
  try {
    json = (await res.json()) as ProductsJson;
  } catch (e) {
    console.warn("fetchCustomerProducts: response was not valid JSON", e);
    return { products: [], hasMore: false };
  }

  const raw = json?.data;
  const isProductObject = (o: unknown): o is Record<string, unknown> =>
    Boolean(o && typeof o === "object" && !Array.isArray(o));
  const data = Array.isArray(raw)
    ? raw
    : isProductObject(raw) &&
        ("id" in raw ||
          "product_id" in raw ||
          "uuid" in raw ||
          "productId" in raw)
      ? [raw as ApiProduct]
      : [];
  const products: Product[] = [];
  for (const item of data) {
    if (!item || typeof item !== "object") continue;
    try {
      products.push(apiProductToProduct(item as ApiProduct, bucketBase));
    } catch (e) {
      console.warn("fetchCustomerProducts: skipped invalid product row", e);
    }
  }
  const hasMore = products.length === limit;
  const unique = dedupeProductsById(products);

  return { products: unique, hasMore };
}

/** Fetch a single product by id (customer-products?id=…&product_id=…) */
export async function fetchCustomerProductById(
  apiBase: string,
  bucketBase: string,
  productId: string,
): Promise<Product | null> {
  const trimmed =
    typeof productId === "string" ? productId.trim() : String(productId).trim();
  if (!trimmed || !isValidCustomerProductIdQuery(trimmed)) {
    return null;
  }
  const result = await fetchCustomerProducts(apiBase, bucketBase, {
    limit: 1,
    offset: 0,
    id: trimmed,
  });
  return result.products[0] ?? null;
}
